import { isEmpty } from 'ramda';
import { LOCATION_CHANGE, LocationChangeAction, push } from 'react-router-redux';
import { AnyAction } from 'redux';
import { ActionsObservable, combineEpics, ofType, StateObservable } from 'redux-observable';
import { of } from 'rxjs';
import { ajax, AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import produce from 'immer';

import {
  getEstimatedProgramFee,
  getEstimatedProgramSavings,
  getEstimatedSettlementAmount,
  getTotalDebt,
} from './onboardSelectors';
import { AscendState } from '..';
import {
  ERROR_AGREEMENT,
  ERROR_SUBMIT_DETAILS,
  FOUND_USER_INFO,
  LOGGING_OUT,
  SAVE_OR_SUBMIT,
  SET_CURRENT_STEP,
  SUBMIT_DETAILS,
  SUCCESS_SUBMIT_AGREEMENT,
  SUCCESS_SUBMIT_DETAILS,
} from '../actions';
import config from '../../config';

// Reducer
export type OnboardState = {
  currentStep: number;
  agreementPage: number;
  details: {
    loading: boolean;
    data: any;
    error?: AjaxError;
  };
  clientAgreement: {
    loading: boolean;
    error?: AjaxError;
  };
};

const defaultState: OnboardState = {
  currentStep: 2,
  agreementPage: 1,
  details: {
    loading: false,
    data: {},
  },
  clientAgreement: {
    loading: false,
  },
};
export const onboardReducer = (state = defaultState, action: AnyAction): OnboardState =>
  produce(state, (draftState) => {
    switch (action.type) {
      case SET_CURRENT_STEP: {
        draftState.currentStep = action.step;
        return;
      }
      case SUBMIT_DETAILS: {
        draftState.details.loading = true;
        return;
      }
      case SUCCESS_SUBMIT_DETAILS: {
        draftState.currentStep = 1;
        draftState.details.data = action.data;
        draftState.details.loading = false;
        return;
      }
      case ERROR_SUBMIT_DETAILS: {
        draftState.details.error = action.error;
        draftState.details.loading = false;
        return;
      }
      case SAVE_OR_SUBMIT: {
        draftState.clientAgreement.loading = true;
        draftState.clientAgreement.error = undefined;
        return;
      }
      case SUCCESS_SUBMIT_AGREEMENT: {
        draftState.clientAgreement.loading = false;
        draftState.clientAgreement.error = undefined;
        if (action.submitted) {
          draftState.currentStep = 3;
        }
        return;
      }
      case ERROR_AGREEMENT: {
        draftState.clientAgreement.error = action.error;
        draftState.clientAgreement.loading = false;
        return;
      }
      case FOUND_USER_INFO: {
        const page = action.data.currentPage;
        draftState.agreementPage = page ? Number(page) : 1;
        return;
      }
    }
  });

// Epics
const routeToOnboardOnUrlChange$ = (
  action$: ActionsObservable<LocationChangeAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(LOCATION_CHANGE),
    withLatestFrom(state$),
    filter(
      ([action, state]) =>
        // localStorage.getItem('isAdmin') !== 'true' &&
        '/onboard' !== action.payload.pathname &&
        !isEmpty(state.user.data) &&
        !state.user.data.submittedAgreement &&
        state.user.data.showPortalCsa
    ),
    map(() => push('/onboard'))
  );

const routeToOnboardOnInitialLoad$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(FOUND_USER_INFO),
    withLatestFrom(state$),
    filter(
      ([action, state]) =>
        // localStorage.getItem('isAdmin') !== 'true' &&
        !action.data.submittedAgreement && state.user.data.showPortalCsa
    ),
    map(() => push('/onboard'))
  );

const submitDetails$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(SUBMIT_DETAILS),
    switchMap((action) => {
      const body = { details: action.details };
      return of(new AjaxResponse({} as any, {} as any, {} as any));
      // return ajax.post(`${config.apiUrl}/onboard`, body, headers);
    }),
    map((res: AjaxResponse) => ({
      type: SUCCESS_SUBMIT_DETAILS,
      data: res.response,
    })),
    catchError((error: AjaxError) => of({ type: ERROR_SUBMIT_DETAILS, error }))
  );

const saveAndExit$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(SUCCESS_SUBMIT_AGREEMENT),
    filter((action) => !action.submitted),
    map(() => ({ type: LOGGING_OUT }))
  );

const parseNumber = (value: any) => {
  if (value) {
    const numAsString = value.toString();
    return numAsString.replace(/[,]/g, '');
  } else {
    return value;
  }
};
const parseDebts = (debts: any) =>
  debts.map((debt: any) => ({
    ...debt,
    balanceAtEnrollment: parseNumber(debt.balanceAtEnrollment),
  }));

const saveOrSubmit$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(SAVE_OR_SUBMIT),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const url = `${config.apiUrl}/onboard`;
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      };

      const now = new Date();
      const formattedDate = now.getMonth() + 1 + '/' + now.getDate() + '/' + now.getFullYear();

      const formValues = state.form.clientAgreement.values!;
      const body = {
        ...formValues,
        initialDraftAmount: parseNumber(formValues.initialDraftAmount),
        draftAmount: parseNumber(formValues.draftAmount),
        debts: parseDebts(formValues.debts),
        currentPage: action.currentPage,
        totalDebt: getTotalDebt(state),
        feeMethod: 'Percentage of enrolled debt',
        estimatedSettlementAmount: getEstimatedSettlementAmount(state),
        estimatedProgramFee: getEstimatedProgramFee(state),
        estimatedProgramFeePercent: state.user.data.ascendFee || 15,
        estimatedProgramLength: state.user.data.programLengthMonths,
        programLengthPayments: state.user.data.programLengthPayments,
        estimatedProgramSavings: getEstimatedProgramSavings(state),
        middleInitial: formValues.middleName ? formValues.middleName.substring(0, 1) : '',
        submitted: action.submitted,
        dateSigned: state.user.data.dateSigned || formattedDate,
      };
      const ajaxRequest = { url, body, headers, method: 'POST', withCredentials: true };
      return ajax(ajaxRequest).pipe(
        map(() => ({ type: SUCCESS_SUBMIT_AGREEMENT, submitted: action.submitted })),
        catchError((error: AjaxError) => of({ type: ERROR_AGREEMENT, error }))
      );
    })
  );

export const onboardEpic = combineEpics(
  routeToOnboardOnUrlChange$,
  saveAndExit$,
  routeToOnboardOnInitialLoad$,
  submitDetails$,
  saveOrSubmit$
);
